export const wait = (ms) => new Promise((res) => setTimeout(res, ms));

export const createSelectionModel = (ids = [], type = "include") => ({
  type: type === "exclude" ? "exclude" : "include",
  ids: new Set((ids || []).map((x) => String(x))),
});

export const selectionModelIds = (model) => {
  return Array.from(model?.ids || []).map((x) => String(x));
};

export const normalizeWhitespace = (str = "") => str.trim().replace(/\s+/g, " ");

// 親名が含まれていても一致できるよう、最下位の語（種小名/亜種小名など）を優先して取り出す
// ただし単語1語だけの属・上位階級も落とさない
export const normalizeNameForComparison = (rawName, parentName = '') => {
  if (!rawName) return undefined;

  // 余計な記号を除去してからスペースを正規化
  let name = String(rawName || '')
    .replace(/[×✕]/g, ' ')
    .replace(/[()]/g, ' ')
    .replace(/[,;]+/g, ' ');
  name = normalizeWhitespace(name);

  const parent = normalizeWhitespace(parentName || '').toLowerCase();
  let tokens = name.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return undefined;

  // 親名に一致するトークンを除去
  if (parent) {
    tokens = tokens.filter(t => t.toLowerCase() !== parent);
  }
  if (tokens.length === 0) return undefined;

  const rankTokenRegex = /^(subsp\.?|ssp\.?|var\.?|f\.?|forma|subvar\.?|subf\.?)$/i;
  const undeterminedRegex = /^(sp|spec|nr|cf|aff)([._-]?[a-z0-9-]+)?\.?$/i; // sp., sp.1, sp.cnc 等を除外
  const nobleParticles = new Set(['van', 'von', 'de', 'da', 'del', 'della', 'der', 'den', 'di', 'la', 'le', 'du', 'des', 'dos', 'das', 'af', 'van der', 'von der']);

  // 単語1語だけなら、その語を採用（未同定語は除外）
  if (tokens.length === 1) {
    const lower = tokens[0].toLowerCase();
    if (undeterminedRegex.test(lower)) return undefined;
    return lower;
  }

  let genusCandidate = null;
  let epithetCandidate = null;

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    const lower = token.toLowerCase();

    if (rankTokenRegex.test(lower)) continue;
    if (undeterminedRegex.test(lower)) return undefined;

    const isUpperStart = /^[A-Z]/.test(token);
    const isLowerStart = /^[a-z]/.test(token);

    // 先頭大文字が続く、または貴族粒子が続く場合は属レベルの可能性が高いので保持
    if (i === 0 && isUpperStart) {
      genusCandidate = lower;
      continue;
    }
    if (i === 1 && genusCandidate && nobleParticles.has(lower)) {
      // Genus van X ... → 粒子はスキップ
      continue;
    }

    // 著者と思われる大文字語に到達したら打ち切り
    if (isUpperStart && epithetCandidate) {
      break;
    }

    // 下位語候補（小文字始まり、あるいは genus 以降の小文字）
    if (isLowerStart) {
      epithetCandidate = lower;
      continue;
    }
  }

  // 種・亜種等の語尾が見つかればそれを返す
  if (epithetCandidate && !undeterminedRegex.test(epithetCandidate)) {
    return epithetCandidate;
  }

  // 語尾が見つからない場合でも、属など単語があるならそれを返して落とさない
  if (genusCandidate && !undeterminedRegex.test(genusCandidate)) {
    return genusCandidate;
  }

  return undefined;
};

export const getGbifAuthorship = (g = {}) => {
  return g.authorship
    || g.canonicalNameAuthorship
    || g.basionymAuthorship
    || g.combinationAuthorship
    || '';
};

export const getGbifYear = (g = {}) => {
  const direct =
    g.publishedInYear
    || g.year
    || g.taxonomicYear;
  if (direct) return direct;

  // 著者情報の末尾に付与された年を推定で抽出する（例: "(Seyrig, 1952)" → 1952）
  const auth = getGbifAuthorship(g);
  if (auth) {
    const m = auth.match(/(1[5-9][0-9]{2}|20[0-9]{2})\)?\s*$/);
    if (m) return m[1];
  }
  return null;
};

export const getGbifDisplayName = (g = {}) => {
  return g.canonicalName || g.scientificName || '';
};

// 親名・子名を正規化してマージキーを生成する
export const buildNameKey = ({ name, parentName }) => {
  const normalizedChild = normalizeNameForComparison(name, parentName);
  const normalizedParent = normalizeNameForComparison(parentName);
  if (!normalizedChild) return null;
  return `${normalizedParent || 'root'}||${normalizedChild}`;
};

export const lineBg = (label) => {
  if (label === 'MERGED') return 'rgba(255,255,255,0.02)';
  if (label === 'DB') return 'rgba(255,255,255,0.04)';
  if (label === 'GBIF') return 'rgba(255,255,255,0.08)';
  return 'transparent';
};
